import { keepTogether } from '../../domain/time';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, BedDouble, CalendarPlus, Check, CheckSquare, Copy, Link2, Loader2, MapPinned, Pencil, PiggyBank, Plane, Receipt, Share2, Ticket, Trash2, UserPlus, Users, X,
} from 'lucide-react';
import { useApp } from '../../data/AppProvider';
import { errorMessage, keys } from '../../data/hooks';
import { DataError } from '../../data/source';
import type { ChatAction, ItemInput } from '../../domain/assistant';
import { formatMoney } from '../../domain/money';
import { applyAction, toMinor } from '../../lib/applyAction';
import { cx } from '../ui';

const META: Record<string, { icon: ReactNode; confirm: string; tone: string }> = {
  create_trip: { icon: <Plane className="size-4" />, confirm: 'Create trip', tone: 'bg-ocean-50 text-ocean-700' },
  update_trip: { icon: <Pencil className="size-4" />, confirm: 'Apply change', tone: 'bg-ocean-50 text-ocean-700' },
  set_stops: { icon: <MapPinned className="size-4" />, confirm: 'Update route', tone: 'bg-ocean-50 text-ocean-700' },
  add_itinerary_items: { icon: <CalendarPlus className="size-4" />, confirm: 'Add to itinerary', tone: 'bg-forest-50 text-forest-700' },
  update_itinerary_item: { icon: <Pencil className="size-4" />, confirm: 'Apply change', tone: 'bg-forest-50 text-forest-700' },
  remove_itinerary_item: { icon: <Trash2 className="size-4" />, confirm: 'Remove item', tone: 'bg-coral-50 text-coral-700' },
  add_booking: { icon: <Ticket className="size-4" />, confirm: 'Save booking', tone: 'bg-turquoise-50 text-turquoise-700' },
  add_expense: { icon: <Receipt className="size-4" />, confirm: 'Record expense', tone: 'bg-sun-50 text-gold-ink' },
  add_checklist_items: { icon: <CheckSquare className="size-4" />, confirm: 'Add to checklist', tone: 'bg-forest-50 text-forest-700' },
  set_budget: { icon: <PiggyBank className="size-4" />, confirm: 'Save budget', tone: 'bg-sun-50 text-gold-ink' },
  invite_to_trip: { icon: <UserPlus className="size-4" />, confirm: 'Send invitations', tone: 'bg-coral-50 text-coral-700' },
  share_trip_with_group: { icon: <Share2 className="size-4" />, confirm: 'Share trip', tone: 'bg-coral-50 text-coral-700' },
  create_group: { icon: <Users className="size-4" />, confirm: 'Create group', tone: 'bg-coral-50 text-coral-700' },
  invite_to_group: { icon: <UserPlus className="size-4" />, confirm: 'Create invitations', tone: 'bg-coral-50 text-coral-700' },
};
const GROUP_KINDS = new Set(['create_group', 'invite_to_group']);

function fmtDate(d: string | null | undefined): string {
  if (!d) return '';
  const dt = new Date(`${d}T12:00:00Z`);
  return keepTogether(new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(dt));
}
const city = (tz: string | null | undefined) => (tz ? tz.split('/').pop()!.replace(/_/g, ' ') : '');

function ItemLine({ it }: { it: Partial<ItemInput> }) {
  const route = it.origin_name || it.destination_name ? `${it.origin_name ?? '?'} → ${it.destination_name ?? '?'}` : it.venue ?? '';
  const when = it.start_date ? `${fmtDate(it.start_date)}${it.start_time ? ` · ${it.start_time}` : ''}${it.end_time ? `–${it.end_time}` : ''}${it.start_time && it.start_timezone ? ` ${city(it.start_timezone)} time` : ''}` : 'Time not set';
  return (
    <li className="flex gap-3 py-1.5">
      <span className="w-28 shrink-0 text-xs leading-5 text-muted">{when}</span>
      <span className="min-w-0 flex-1 text-sm">
        <span className="font-medium">{it.title}</span>
        {route && <span className="block text-xs text-muted break-anywhere">{route}{it.operator || it.service_number ? ` · ${[it.operator, it.service_number].filter(Boolean).join(' ')}` : ''}</span>}
        {it.status === 'suggested' && <span className="mt-0.5 inline-block rounded-full bg-sun-50 px-2 text-[0.6875rem] font-medium text-gold-ink">Idea — not booked</span>}
      </span>
    </li>
  );
}

function Details({ a }: { a: ChatAction }) {
  const p = a.payload as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  switch (a.kind) {
    case 'create_trip':
      return (
        <div className="text-sm">
          <p className="font-semibold">{p.title}</p>
          <p className="text-muted">{p.date_mode === 'unknown' ? (p.date_note ?? 'Dates not decided') : `${fmtDate(p.start_date)} – ${fmtDate(p.end_date)}${p.date_mode === 'flexible' ? ' (flexible)' : ''}`}{p.origin_city ? ` · from ${p.origin_city}` : ''}</p>
          {p.stops?.length > 0 && <Route stops={p.stops} />}
        </div>
      );
    case 'set_stops': return <Route stops={p.stops} />;
    case 'update_trip':
      return <ul className="text-sm">{Object.entries(p).filter(([, v]) => v != null).map(([k, v]) => <li key={k}><span className="text-muted">{k.replace(/_/g, ' ')}:</span> {String(v)}</li>)}</ul>;
    case 'add_itinerary_items':
      return <ul className="divide-y divide-line">{(p.items as ItemInput[]).map((it, i) => <ItemLine key={i} it={it} />)}</ul>;
    case 'update_itinerary_item':
      return <ul className="divide-y divide-line"><ItemLine it={p.changes} /></ul>;
    case 'remove_itinerary_item':
      return <p className="text-sm">Remove <strong>{p.title}</strong> from the itinerary. This does not cancel anything with the provider.</p>;
    case 'add_booking': {
      const b = p.booking ?? {};
      return (
        <div>
          <ul><ItemLine it={p.item} /></ul>
          <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
            {b.provider_name && <><dt className="text-muted">Booked with</dt><dd>{b.provider_name}</dd></>}
            {b.reference && <><dt className="text-muted">Reference</dt><dd className="font-mono text-[0.8125rem]">{b.reference}</dd></>}
            {b.price && b.currency && <><dt className="text-muted">Price</dt><dd>{formatMoney(toMinor(b.price, b.currency), b.currency)}</dd></>}
          </dl>
          {p.uncertain_fields?.length > 0 && (
            <p className="mt-2 flex gap-2 rounded-xl bg-warn-soft p-2 text-xs text-warn"><AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />Check before saving: {p.uncertain_fields.join(', ')}. Elio couldn’t read these with confidence.</p>
          )}
          {p.source === 'uploaded_file' && <p className="mt-1 text-xs text-muted">Read from your file. Your reference and price stay private to you.</p>}
        </div>
      );
    }
    case 'add_expense':
      return (
        <p className="text-sm"><strong>{formatMoney(toMinor(p.amount, p.currency), p.currency)}</strong> · {p.description}<span className="block text-xs text-muted">{fmtDate(p.occurred_on)} · {String(p.category).replace(/_/g, ' ')} · {p.split === 'equal_trip_members' ? 'split equally with the trip' : 'just you'}</span></p>
      );
    case 'add_checklist_items':
      return <ul className="space-y-1 text-sm">{(p.items as { title: string; due_on: string | null }[]).map((it, i) => <li key={i} className="flex gap-2"><CheckSquare className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />{it.title}{it.due_on && <span className="text-xs text-muted">· by {fmtDate(it.due_on)}</span>}</li>)}</ul>;
    case 'set_budget':
      return (
        <div className="text-sm">
          {p.total_limit && <p>Budget: <strong>{formatMoney(toMinor(p.total_limit, p.currency), p.currency)}</strong></p>}
          <ul className="mt-1 divide-y divide-line">{(p.estimates as { category: string; description: string; low: string; high: string; basis: string }[]).map((e, i) => (
            <li key={i} className="flex justify-between gap-3 py-1"><span className="min-w-0">{e.description}<span className="block text-xs text-muted">{e.basis}</span></span><span className="shrink-0 tabular-nums">{formatMoney(toMinor(e.low, p.currency), p.currency)}–{formatMoney(toMinor(e.high, p.currency), p.currency)}</span></li>
          ))}</ul>
          <p className="mt-1 text-xs text-muted">Estimates, not quotes.</p>
        </div>
      );
    case 'invite_to_trip':
      return (
        <div className="text-sm">
          <ul className="flex flex-wrap gap-1.5">{(p.people as { name: string }[]).map((x, i) => <li key={i} className="rounded-full bg-coral-50 px-2.5 py-0.5 text-coral-700">{x.name}</li>)}</ul>
          <p className="mt-2 text-xs text-muted">{p.role === 'guest' ? 'As read-only guests. ' : ''}They get an invitation in the app and choose whether to join. Your private bookings and documents stay private.</p>
        </div>
      );
    case 'share_trip_with_group':
      return <p className="text-sm">Share this trip with <strong>{p.group_name}</strong>. Nobody is added yet: you can then invite members to it. Private bookings and documents stay private.</p>;
    case 'create_group':
      return (
        <div className="text-sm">
          <p className="font-semibold">{p.name}</p>
          {p.description && <p className="text-muted">{p.description}</p>}
          <p className="mt-1 text-xs text-muted">You’ll be the owner.{p.invite_emails?.length ? ` Invites ${p.invite_emails.join(', ')}.` : ''}</p>
        </div>
      );
    case 'invite_to_group':
      return (
        <div className="text-sm">
          <p>Invite to <strong>{p.group_name}</strong>{p.role === 'admin' ? ' as admins' : ''}:</p>
          {p.emails?.length ? <ul className="mt-1 flex flex-wrap gap-1.5">{(p.emails as string[]).map((e) => <li key={e} className="rounded-full bg-ivory-dark px-2.5 py-0.5 break-anywhere">{e}</li>)}</ul>
            : <p className="text-muted">One invitation link you can share yourself (valid for 7 days).</p>}
        </div>
      );
    default: return null;
  }
}

/** Invitation links are shown once, right after confirming, so the traveller can copy them. */
function InviteLinks({ links }: { links: { label: string; url: string }[] }) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (url: string) => { try { await navigator.clipboard.writeText(url); setCopied(url); } catch { setCopied(null); } };
  return (
    <ul className="mt-2 space-y-1.5">
      {links.map((l) => (
        <li key={l.url} className="flex items-center gap-2 rounded-xl bg-ivory p-2 text-sm">
          <Link2 className="size-4 shrink-0 text-muted" aria-hidden />
          <span className="min-w-0 flex-1"><span className="block text-xs text-muted">{l.label}</span><span className="block truncate font-mono text-xs" title={l.url}>{l.url}</span></span>
          <button type="button" onClick={() => void copy(l.url)} className="inline-flex min-h-9 shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-white px-3 text-xs font-semibold text-forest-700 shadow-sm"><Copy className="size-3.5" aria-hidden />{copied === l.url ? 'Copied' : 'Copy'}</button>
        </li>
      ))}
    </ul>
  );
}

function Route({ stops }: { stops: { city: string; arrive_on?: string | null; depart_on?: string | null }[] }) {
  return (
    <ol className="mt-2 flex flex-wrap items-center gap-1.5">
      {stops.map((s, i) => (
        <li key={i} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-muted" aria-hidden>→</span>}
          <span className="rounded-full bg-ocean-50 px-2.5 py-0.5 text-sm text-ocean-700">{s.city}{s.arrive_on && <span className="ml-1 text-xs opacity-75">{fmtDate(s.arrive_on)}</span>}</span>
        </li>
      ))}
    </ol>
  );
}

export function ActionCard({ action, conversationId, conversationTripId, onTripCreated }: {
  action: ChatAction; conversationId: string; conversationTripId: string | null; onTripCreated?: (tripId: string) => void;
}) {
  const { data, announce } = useApp();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<'apply' | 'dismiss' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [links, setLinks] = useState<{ label: string; url: string }[] | null>(null);
  const meta = META[action.kind] ?? META.add_itinerary_items!;
  const decided = action.status === 'applied' || action.status === 'dismissed';
  const tripLink = (action.result?.tripId as string | undefined) ?? action.trip_id ?? null;

  const refresh = async (tripId?: string | null, groupId?: string | null) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: keys.conversation(conversationId) }),
      qc.invalidateQueries({ queryKey: keys.trips }),
      qc.invalidateQueries({ queryKey: keys.groups }),
      ...(groupId ? [qc.invalidateQueries({ queryKey: keys.group(groupId) })] : []),
      ...(tripId ? [qc.invalidateQueries({ queryKey: keys.trip(tripId) }), qc.invalidateQueries({ queryKey: keys.bookings(tripId) }), qc.invalidateQueries({ queryKey: keys.checklist(tripId) }), qc.invalidateQueries({ queryKey: keys.budget(tripId) })] : []),
    ]);
  };

  const confirm = async () => {
    setBusy('apply'); setError(null);
    try {
      const { links: newLinks, ...result } = await applyAction(data, action);
      if (newLinks?.length) setLinks(newLinks);
      await data.decideAction(action.id, 'applied', result as Record<string, unknown>);
      if (action.kind === 'create_trip' && result.tripId && !conversationTripId) {
        await data.setConversationTrip(conversationId, result.tripId).catch(() => undefined);
        onTripCreated?.(result.tripId);
      }
      announce(GROUP_KINDS.has(action.kind) ? 'Done.' : 'Done — your trip is updated.');
      await refresh(result.tripId ?? action.trip_id, result.groupId);
    } catch (e) {
      const msg = e instanceof DataError ? e.message : e instanceof Error ? e.message : errorMessage(e);
      setError(msg);
      await data.decideAction(action.id, 'failed', null, msg.slice(0, 900)).catch(() => undefined);
      await qc.invalidateQueries({ queryKey: keys.conversation(conversationId) });
    } finally { setBusy(null); }
  };
  const dismiss = async () => {
    setBusy('dismiss');
    try { await data.decideAction(action.id, 'dismissed'); await qc.invalidateQueries({ queryKey: keys.conversation(conversationId) }); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className={cx('fade-in mt-3 overflow-hidden rounded-2xl border bg-white', action.status === 'applied' ? 'border-forest-200' : action.status === 'dismissed' ? 'border-line opacity-70' : 'border-line shadow-[var(--shadow-card)]')}>
      <div className="flex items-start gap-3 border-b border-line px-4 py-3">
        <span className={cx('flex size-8 shrink-0 items-center justify-center rounded-xl', meta.tone)}>{meta.icon}</span>
        <div className="min-w-0 flex-1">
          <p className="text-[0.8125rem] font-semibold uppercase tracking-wide text-muted">{action.status === 'applied' ? 'Done' : action.status === 'dismissed' ? 'Not added' : 'Ready for you to confirm'}</p>
          <p className="text-[0.9375rem] font-medium leading-snug break-anywhere">{action.summary}</p>
        </div>
        {action.status === 'applied' && <span className="flex size-7 items-center justify-center rounded-full bg-forest-700 text-white"><Check className="size-4" aria-hidden /></span>}
      </div>
      <div className="px-4 py-3">
        <Details a={action} />
        {action.status === 'applied' && typeof action.result?.note === 'string' && <p className="mt-2 text-sm text-forest-800">{action.result.note}</p>}
        {action.status === 'applied' && links && <InviteLinks links={links} />}
        {action.status === 'applied' && !links && Number(action.result?.linkCount ?? 0) > 0 ? <p className="mt-1 text-xs text-muted">For your security, invitation links are shown only once. Need another? Ask Elio or use Invite on the group page.</p> : null}
      </div>
      {(error || action.status === 'failed') && <p className="mx-4 mb-3 rounded-xl bg-danger-soft p-2 text-sm text-danger" role="alert">{error ?? action.error ?? 'This could not be applied.'}</p>}
      <div className="flex flex-wrap items-center gap-2 border-t border-line bg-ivory/60 px-4 py-2.5">
        {!decided ? (
          <>
            <button type="button" onClick={confirm} disabled={Boolean(busy)} className="inline-flex min-h-10 items-center gap-2 rounded-full bg-forest-700 px-4 text-sm font-semibold text-white hover:bg-forest-800 disabled:opacity-60">
              {busy === 'apply' ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Check className="size-4" aria-hidden />}{action.status === 'failed' ? 'Try again' : meta.confirm}
            </button>
            <button type="button" onClick={dismiss} disabled={Boolean(busy)} className="inline-flex min-h-10 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-muted hover:bg-white hover:text-ink">
              <X className="size-4" aria-hidden />Not now
            </button>
          </>
        ) : action.status === 'applied' && GROUP_KINDS.has(action.kind) && typeof action.result?.groupId === 'string' ? (
          <Link to={`/groups/${action.result.groupId}`} className="inline-flex min-h-10 items-center gap-1.5 text-sm font-semibold text-forest-700 hover:underline"><Users className="size-4" aria-hidden />Open the group</Link>
        ) : action.status === 'applied' && tripLink ? (
          <Link to={action.kind === 'invite_to_trip' || action.kind === 'share_trip_with_group' ? `/trips/${tripLink}/people` : action.kind === 'add_booking' ? `/trips/${tripLink}/bookings` : action.kind === 'add_checklist_items' ? `/trips/${tripLink}/checklist` : action.kind === 'add_expense' || action.kind === 'set_budget' ? `/trips/${tripLink}/budget` : `/trips/${tripLink}`}
            className="inline-flex min-h-10 items-center gap-1.5 text-sm font-semibold text-forest-700 hover:underline">
            {action.kind === 'add_booking' ? <BedDouble className="size-4" aria-hidden /> : <MapPinned className="size-4" aria-hidden />}View in trip
          </Link>
        ) : <span className="text-sm text-muted">You can ask Elio to prepare it again any time.</span>}
      </div>
    </div>
  );
}
